<template>
  <div>
    <v-container fluid grid-list-md>
      <v-layout>
        <v-flex>
          <v-btn @click="back()">Back</v-btn>
        </v-flex>
      </v-layout>
    </v-container>

    <v-container v-if="loading" fluid fill-height justify-center align-center>
      <v-progress-circular indeterminate color="indigo darken-4"></v-progress-circular>
    </v-container>

    <v-container v-else fluid grid-list-md>
      <v-layout wrap>
        <v-flex wrap xs12>
          <v-card>
            <v-card-title>
              <v-flex>
                <h1>{{ tournament.shortNameOrName }}</h1>
                <v-switch label="Live matches only" @click="toggleLiveFilter" :input-value="liveFilter"></v-switch>
              </v-flex>
            </v-card-title>

            <v-data-table :headers="headers" :pagination.sync="pagination" :items="filteredMatches" :loading="loading" hide-actions>
              <template slot="items" slot-scope="{ item }">
                <tr @click="rowClick(item)" style="cursor: pointer">
                  <td class="hidden-sm-and-down">{{ `${item.noInTournament}`.padStart(3, '0') }}</td>
                  <td>{{ formatDate(item.dateTimeUtc) }}</td>
                  <td class="hidden-sm-and-down">{{ item.countryName }}</td>
                  <td class="hidden-sm-and-down">{{ item.city }}</td>
                  <td class="hidden-sm-and-down">{{ item.poolName }}</td>
                  <td>
                    <div style="display:flex; align-items: center">
                      <template v-if="item.teamA">
                        <f-flag :code="item.teamA.code" :height="20"></f-flag>
                        {{ item.teamA.name }}
                      </template>
                      <template v-else>
                        {{ item.teamACalculatedName }}
                      </template>
                    </div>
                  </td>
                  <td>
                   <div style="display:flex; align-items: center">
                      <template v-if="item.teamB">
                        <f-flag :code="item.teamB.code" :height="20"></f-flag>
                        {{ item.teamB.name }}
                      </template>
                      <template v-else>
                        {{ item.teamBCalculatedName }}
                      </template>
                    </div>
                  </td>
                  <td class="hidden-sm-and-down">
                    <strong style="margin-right: 10px">{{ item.matchResultText }}</strong>
                    <span>{{ item.setsResultsText }}</span>
                  </td>
                  <td class="hidden-sm-and-down">{{ status[item.status] }}</td>
                  <td class="hidden-sm-and-down">
                    <v-btn
                      color="blue-grey"
                      class="white--text"
                      v-if="item.noDocumentP2"
                      small
                      :href="`https://www.fivb.org/vis2009/getdocument.asmx?no=${item.noDocumentP2}`"
                      target="_blank"
                    >
                      P2 Match Report
                      <v-icon right dark>cloud_download</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>

        <v-flex wrap xs12>
          <v-card>
            <v-card-title primary-title style="display: flex; align-items: center; justify-content: space-between">
              <h2>Phase Standings</h2>
            </v-card-title>
            <v-card-title style="display: flex; align-items: center; padding: 0 16px">
              <v-select
                style="width: 15%; flex: inherit"
                v-model="selectedPhase"
                :items="phaseSelector"
              ></v-select>
            </v-card-title>

            <v-data-table :headers="poolsHeaders" :pagination.sync="poolPagination" :items="poolsData" hide-actions>
              <template slot="items" slot-scope="{ item }">
                <tr>
                  <td style="text-align: center">
                    <strong>{{ item.rankText }}</strong>
                  </td>
                  <td style="text-align: center">
                    <strong>{{ item.sourcePoolCode }}{{ item.sourcePoolRank }}</strong>
                  </td>
                  <td>
                    <div style="display:flex; align-items: center">
                      <f-flag :code="item.teamCode" :height="20"></f-flag>
                      <strong>{{ item.teamName }}</strong>
                    </div>
                  </td>
                  <td style="text-align: center">
                    <strong>{{ item.matchesWon }}</strong>
                  </td>
                  <td style="text-align: center">{{ item.matchesLost }}</td>
                  <td style="text-align: center">{{ item.matches }}</td>
                  <td></td>
                  <td style="text-align: center">
                    <strong>{{ item.matchPoints }}</strong>
                  </td>
                  <td style="text-align: center">{{ item.matchesAt30 }}</td>
                  <td style="text-align: center">{{ item.matchesAt31 }}</td>
                  <td style="text-align: center">{{ item.matchesAt32 }}</td>
                  <td style="text-align: center">{{ item.matchesAt23 }}</td>
                  <td style="text-align: center">{{ item.matchesAt13 }}</td>
                  <td style="text-align: center">{{ item.matchesAt03 }}</td>
                  <td></td>
                  <td style="text-align: center">{{ item.setsWon }}</td>
                  <td style="text-align: center">{{ item.setsLost }}</td>
                  <td style="text-align: center">
                    <strong>{{ item.setsRatioText }}</strong>
                  </td>
                  <td></td>
                  <td style="text-align: center">{{ item.pointsWon }}</td>
                  <td style="text-align: center">{{ item.pointsLost }}</td>
                  <td style="text-align: center">
                    <strong>{{ item.pointsRatioText }}</strong>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-card>
        </v-flex>

        <v-flex wrap xs7>
          <statistics-by-player :tournament-id="$route.params.id"></statistics-by-player>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { format } from 'date-fns'
import { Request, QueryBuilder } from '@fivb/sdk'

import Flag from '@/components/Flag'
import MatchStatus from '@/matchStatus'
import StatisticsByPlayer from './TournamentMatchList/StatisticsByPlayer/StatisticsByPlayer'

export default {
  data: () => ({
    loading: true,
    selectedPhase: null,
    phaseSelector: [],
    poolsData: [],
    status: MatchStatus,
    poolPagination: {
      sortBy: 'position',
      rowsPerPage: -1,
    },
    poolsHeaders: [
      { text: 'Rank', value: 'rankText', sortable: false, class: 'text-xs-center' },
      { text: 'Pool Rank', value: 'SourcePoolCode', sortable: false, },
      { text: 'Name', value: 'teamName', sortable: false, },
      { text: 'Won', value: 'matchesWon', sortable: false, class: 'text-xs-center' },
      { text: 'Lost', value: 'matchesLost', sortable: false, class: 'text-xs-center' },
      { text: 'Total', value: 'matches', sortable: false, class: 'text-xs-center' },
      { text: '', value: 'sep1', sortable: false, class: 'text-xs-center' },
      { text: 'Points', value: 'matchPoints', sortable: false, class: 'text-xs-center' },
      { text: '3-0', value: 'matchesAt30', sortable: false, class: 'text-xs-center' },
      { text: '3-1', value: 'matchesAt31', sortable: false, class: 'text-xs-center' },
      { text: '3-2', value: 'matchesAt32', sortable: false, class: 'text-xs-center' },
      { text: '2-3', value: 'matchesAt23', sortable: false, class: 'text-xs-center' },
      { text: '1-3', value: 'matchesAt13', sortable: false, class: 'text-xs-center' },
      { text: '0-3', value: 'matchesAt03', sortable: false, class: 'text-xs-center' },
      { text: '', value: 'sep2', sortable: false, },
      { text: 'Sets Won', value: 'setsWon', sortable: false, class: 'text-xs-center' },
      { text: 'Sets Lost', value: 'setsLost', sortable: false, class: 'text-xs-center' },
      { text: 'Sets Ratio', value: 'setsRatioText', sortable: false, class: 'text-xs-center' },
      { text: '', value: 'sep3', sortable: false, },
      { text: 'Points Won', value: 'pointsWon', sortable: false, class: 'text-xs-center' },
      { text: 'Points Lost', value: 'pointsLost', sortable: false, class: 'text-xs-center' },
      { text: 'Points Ratio', value: 'pointsRatioText', sortable: false, class: 'text-xs-center' },
    ],
    pagination: {
      sortBy: 'dateTimeUtc',
      rowsPerPage: -1,
    },
    headers: [
      { text: 'No', value: 'noInTournament', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Date', value: 'dateTimeUtc', sortable: true, },
      { text: 'Country', value: 'countryName', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'City', value: 'city', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Phase', value: 'poolName', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Team A', value: 'teamA.name', sortable: true, },
      { text: 'Team B', value: 'teamB.name', sortable: true, },
      { text: 'Score', value: 'matchResultText', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Status', value: 'status', sortable: true, class: 'hidden-sm-and-down', },
      { text: 'Documents', value: 'noDocumentP2', sortable: true, class: 'hidden-sm-and-down', },
    ],
  }),

  components: {
    StatisticsByPlayer,
    'f-flag': Flag,
  },

  async created () {
    const state = this.$store.state.tournamentsList[this.$route.params.id]
    const promises = []

    promises.push(this.fetchData(this.generateMatchesQuery()))

    promises.push(this.fetchData(this.generatePoolListQuery()))

    if (state === undefined) {
     promises.push(this.fetchData(this.generateTournamentQuery()))
    }

    const [matches, pools, tournament] = await Promise.all(promises)

    pools.data.forEach(pool => this.phaseSelector.push({ text: pool.name, value: pool.no }))
    this.selectedPhase = pools.data[0].no

    this.$store.commit('updateOrCreateTournament', {
      code: this.$route.params.id,
      localState: {
        version: matches.version,
        matches: matches.data,
        tournament: tournament ? tournament.data : undefined,
      },
    })

    this.loading = false
  },

  watch: {
    async selectedPhase () {
      const request = new Request('GetVolleyPoolRanking')
        .addAttribute('Fields', 'Position SourcePoolCode SourcePoolRank RankText TeamCode TeamName MatchPoints Matches MatchesWon MatchesLost MatchesAt30 MatchesAt31 MatchesAt32 MatchesAt23 MatchesAt13 MatchesAt03 SetsWon SetsLost SetsRatioText PointsWon PointsLost PointsRatioText')
        .addAttribute('NoPool', this.selectedPhase)
        .toString()

      const { data } = await this.fetchData(request)
      this.poolsData = data
    }
  },

  computed: {
    tournament () {
      return this.$store.state.tournamentsList[this.$route.params.id].tournament
    },

    matches () {
      return this.$store.state.tournamentsList[this.$route.params.id].matches
    },

    liveFilter () {
      return this.$store.state.tournamentsList.liveFilter
    },

    filteredMatches: function () {
      if (!this.liveFilter) {
        return this.matches
      }

      return this.matches.filter((match) => {
        return match.status >= 2 && match.status <= 18
      })
    },
  },

  methods: {
    toggleLiveFilter () {
      this.$store.commit('toggleTournamentLiveFilter')
    },

    back () {
      this.$router.push({ name: 'TournamentList' })
    },

    rowClick ({ no }) {
      this.$router.push({ name: 'MatchLive', params: { id: no } })
    },

    formatDate (date) {
      return format(date, 'MM/DD HH:mm')
    },

    generateTournamentQuery () {
      return new QueryBuilder('VolleyTournament')
        .find(this.$route.params.id, ['ShortNameOrName'])
    },

    generateMatchesQuery () {
      const version = this.$store.state.tournamentsList[this.$route.params.id]
        ? this.$store.state.tournamentsList[this.$route.params.id].version
        : 0

      return new QueryBuilder('VolleyMatch')
        .filterBy('NoTournament', this.$route.params.id)
        .with('TeamA')
        .with('TeamB')
        .fetch(
          ['No', 'City', 'NoDocumentP2', 'NoInTournament', 'CountryName', 'DateTimeUtc', 'PoolName', 'TeamACalculatedName', 'TeamBCalculatedName', 'Status', 'MatchResultText', 'SetsResultsText'],
          version
        )
    },

    generatePoolListQuery () {
      return new QueryBuilder('VolleyPool')
        .filterBy('NoTournament', this.$route.params.id)
        .filterBy('ForRanking', true)
        .fetch(['No', 'Name'])
    },

    fetchData (query) {
      return fetch(process.env.VUE_APP_FIVB_API_URL, {
        method: 'POST',
        mode: 'cors',
        cache: 'no-store',
        headers: new Headers({
          'Content-Type': 'application/xml',
          'Accept': 'application/json',
          'X-FIVB-App-ID': '3b665ad61056495ab5514b81eb0562d8'
        }),
        body: query,
      }).then(response => response.json())
    },
  },
}
</script>




// WEBPACK FOOTER //
// src/components/TournamentMatchList.vue